import * as d3 from 'd3';
import { numberToLocaleString } from '@/lib/utils';
import { msToShortDuration } from '@/lib/dateTime';
import { playerDropCategories } from "@/lib/playerDropCategories";
import { PlayerDropsCategoryCount } from './chartingUtils';
import { TimelineDropsChartData } from './TimelineDropsChart';
import { DrilldownRangeSelectionType } from './PlayerDropsPage';


//Helpers
const translate = (x: number, y: number) => `translate(${x}, ${y})`;

export type TimelineDropsDatum = {
    startDate: Date;
    changes: number;
    drops: PlayerDropsCategoryCount[];
}

type drawDropsTimelineProps = {
    legendRef: HTMLDivElement;
    svgRef: SVGElement;
    canvasRef: HTMLCanvasElement;
    setRenderError: (error: string) => void;
    onHoverDatum?: (datum: TimelineDropsDatum | null) => void;
    size: {
        width: number;
        height: number;
    };
    rangeSelected: DrilldownRangeSelectionType;
    rangeSetter: (range: DrilldownRangeSelectionType) => void;
    margins: {
        top: number;
        right: number;
        bottom: number;
        left: number;
        axis: number;
    };
    isDarkMode: boolean;
    chartName: string;
    data: TimelineDropsChartData,
};

export default function drawDropsTimeline({
    legendRef,
    svgRef,
    canvasRef,
    setRenderError,
    onHoverDatum,
    size: { width, height },
    rangeSelected,
    rangeSetter,
    margins,
    isDarkMode,
    chartName,
    data,
}: drawDropsTimelineProps) {
    //Clear SVG
    d3.select(svgRef).selectAll('*').remove(); // FIXME: Clear SVG

    //Setup selectors
    const svg = d3.select<SVGElement, TimelineDropsDatum>(svgRef);
    if (svg.empty()) throw new Error('SVG selection failed.');
    const canvas = d3.select(canvasRef)!;
    if (canvas.empty()) throw new Error('Canvas selection failed.');
    if (!canvasRef?.getContext) {
        throw new Error(`Canvas not supported.`);
    }

    let isFirstRender = true;
    console.log('From:', data.startDate.toISOString());
    console.log('To:', data.endDate.toISOString());
    console.log('Window:', msToShortDuration(data.startDate.getTime() - data.endDate.getTime()));
    console.log('Number of intervals:', data.log.length);

    //Setup
    const drawableAreaHeight = height - margins.top - margins.bottom;
    const drawableAreaWidth = width - margins.left - margins.right;
    console.log('Drawable area:', drawableAreaWidth, drawableAreaHeight);

    const chartGroup = svg.append('g')
        .attr('transform', translate(margins.left, margins.top));


    //Scales
    const timeScale = d3.scaleTime(
        [data.startDate, data.endDate],
        [0, drawableAreaWidth],
    );
    const countsScaleMaxDomain = data.maxDrops < 10 ? data.maxDrops + 1 : data.maxDrops;
    const countsScale = d3.scaleLinear(
        [0, countsScaleMaxDomain],
        [height - margins.bottom, margins.top],
    );


    //Axis
    const timeAxisTicksScale = d3.scaleLinear([382, 1350], [7, 16]);
    const timeAxis = d3.axisBottom(timeScale)
        .ticks(timeAxisTicksScale(width))
    // .tickFormat(d => d && dateToLocaleTimeString(d as Date, 'numeric', '2-digit'));
    // .tickFormat(d3.timeFormat('%H:%M'));
    const timeAxisGroup = chartGroup.append("g")
        .attr('transform', translate(0, drawableAreaHeight))
        .attr('class', 'time-axis')
        .call(timeAxis);

    // Style time axis
    timeAxisGroup.selectAll('text')
        .attr('fill', isDarkMode ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.45)')
        .attr('font-size', '9px')
        .attr('font-family', 'ui-monospace, monospace')
        .attr('letter-spacing', '0.5px');
    timeAxisGroup.selectAll('.domain, .tick line')
        .attr('stroke', isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.1)');

    const countsAxisTickValues = (countsScaleMaxDomain <= 7) ? d3.range(countsScaleMaxDomain + 1) : null;
    const countsAxis = d3.axisLeft(countsScale)
        .ticks(height > 200 ? 6 : 4)
        .tickFormat(d => numberToLocaleString(d as number))
        .tickValues(countsAxisTickValues as any); //integer values only 
    const countsAxisGroup = svg.append('g')
        .attr('class', 'counts-axis')
        .attr('transform', translate(margins.left - margins.axis, 0))
        .call(countsAxis);

    // Style counts axis
    countsAxisGroup.selectAll('text')
        .attr('fill', isDarkMode ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.45)')
        .attr('font-size', '9px')
        .attr('font-family', 'ui-monospace, monospace');
    countsAxisGroup.selectAll('.domain, .tick line')
        .attr('stroke', isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.1)');



    /**
     * Canvas
     */
    const lodInHours = data.displayLod === 'day' ? 24 : 1;
    const endOfFirstInterval = new Date(data.startDate.getTime() + (lodInHours * 60 * 60 * 1000));
    const intervalWidth = timeScale(endOfFirstInterval) - timeScale(data.startDate);
    const barCenterOffset = Math.floor(intervalWidth / 2);
    const barPadding = data.displayLod === 'day' ? 12 : 0;
    const canvasTicksStyle = isDarkMode ? `rgba(255, 255, 255, 0.15)` : `rgba(0, 0, 0, 0.35)`;
    const canvasBackgroundStyle = isDarkMode ? '#00000035' : '#00000007';
    const changeIndicatorStyle = isDarkMode ? '#FFFFFFE0' : '#000000D0';
    const canvasCountScale = (value: number) => countsScale(value) - margins.top;

    const drawCanvasTimeline = () => {
        //Context preconditions
        const canvasNode = canvas.node();
        if (!canvasNode) return setRenderError('Canvas node not found.');
        const ctx = canvasNode.getContext('2d');
        if (!ctx) return setRenderError('Canvas 2d context not found.');
        if (!intervalWidth) return setRenderError('Cannot render with zero interval width.');

        //Setup
        isFirstRender && console.time('drawing canvas timeline');
        ctx.clearRect(0, 0, drawableAreaWidth, drawableAreaHeight);

        // Premium background - very subtle gradient
        const bgGrad = ctx.createLinearGradient(0, 0, 0, drawableAreaHeight);
        bgGrad.addColorStop(0, isDarkMode ? 'rgba(0,0,0,0.18)' : 'rgba(0,0,0,0.03)');
        bgGrad.addColorStop(1, isDarkMode ? 'rgba(0,0,0,0.06)' : 'rgba(0,0,0,0.01)');
        ctx.fillStyle = bgGrad;
        ctx.fillRect(0, 0, drawableAreaWidth, drawableAreaHeight);

        //Drawing horizontal ticks - subtle dashed lines
        ctx.strokeStyle = isDarkMode ? `rgba(255, 255, 255, 0.06)` : `rgba(0, 0, 0, 0.12)`;
        ctx.lineWidth = 1;
        ctx.setLineDash([3, 4]);
        try {
            const tickLines = countsAxisGroup.selectAll("g.tick").nodes();
            for (const tickLine of tickLines) {
                const translation = (tickLine as any)?.getAttribute('transform');
                if (!translation) continue;
                const y = Math.round(+translation.split(',')[1].replace(')', '') - margins.top) + 0.5;
                ctx.beginPath();
                ctx.moveTo(0, y);
                ctx.lineTo(drawableAreaWidth, y);
                ctx.stroke();
            }
        } catch (error) {
            console.error('Error drawing horizontal ticks:', error);
        }
        ctx.setLineDash([]);

        // Helper: draw a rounded-top rectangle
        const roundedTopRect = (x: number, y: number, w: number, h: number, r: number) => {
            if (h < r * 2) r = Math.floor(h / 2);
            const right = x + w;
            const bottom = y + h;
            ctx.beginPath();
            ctx.moveTo(x + r, y);
            ctx.lineTo(right - r, y);
            ctx.quadraticCurveTo(right, y, right, y + r);
            ctx.lineTo(right, bottom);
            ctx.lineTo(x, bottom);
            ctx.lineTo(x, y + r);
            ctx.quadraticCurveTo(x, y, x + r, y);
            ctx.closePath();
        };

        //Drawing the timeline — premium gradient bars
        for (const intervalData of data.log) {
            const baseX = timeScale(intervalData.startDate);
            if (baseX < 0 || baseX > drawableAreaWidth) continue;

            const supposedStartX = Math.round(baseX);
            const renderStartX = supposedStartX - barCenterOffset + barPadding;
            const renderOffsetX = baseX - renderStartX;
            const barWidth = Math.round(intervalWidth + renderOffsetX) + 1 - barCenterOffset - barPadding;
            if (barWidth < 1) continue;

            // Calculate all segments first so we know the topmost one
            let dropSum = 0;
            let lastRenderTopY = drawableAreaHeight + 1;
            const segments: {
                topY: number; bottomY: number; height: number; color: string; isTop: boolean; cat: string;
            }[] = [];

            const dropsLen = intervalData.drops.length;
            for (let di = 0; di < dropsLen; di++) {
                const [dropCategory, dropCount] = intervalData.drops[di];
                if (!dropCount) continue;
                const barBottomY = lastRenderTopY - 1;
                const barTopY = canvasCountScale(dropSum + dropCount) + 1;
                const barHeight = barBottomY - barTopY;
                const barHeightRnd = Math.max(Math.floor(barHeight), barHeight > 0.8 ? 1 : 0);
                dropSum += dropCount;
                if (barHeightRnd < 1) continue;
                lastRenderTopY = barBottomY - barHeightRnd;

                segments.push({
                    topY: barBottomY - barHeightRnd,
                    bottomY: barBottomY,
                    height: barHeightRnd,
                    color: playerDropCategories[dropCategory]?.color ?? '#ffffff',
                    isTop: di === dropsLen - 1,
                    cat: dropCategory,
                });
            }

            // Draw each segment with gradient fill
            for (let si = 0; si < segments.length; si++) {
                const seg = segments[si];
                const isTopSeg = si === segments.length - 1;

                // Gradient fill: bright at top, dimmer at bottom
                const grad = ctx.createLinearGradient(0, seg.topY, 0, seg.bottomY);
                grad.addColorStop(0, seg.color + (isDarkMode ? 'EE' : 'CC'));
                grad.addColorStop(1, seg.color + (isDarkMode ? '88' : '66'));
                ctx.fillStyle = grad;

                if (isTopSeg && seg.height >= 4) {
                    // Top segment: rounded top corners
                    const radius = Math.min(barWidth <= 8 ? 1 : 3, Math.floor(seg.height / 2));
                    roundedTopRect(renderStartX, seg.topY, barWidth, seg.height, radius);
                    ctx.fill();

                    // Glowing top edge line
                    ctx.save();
                    ctx.strokeStyle = seg.color;
                    ctx.lineWidth = 1.5;
                    ctx.shadowColor = seg.color;
                    ctx.shadowBlur = isDarkMode ? 6 : 3;
                    ctx.globalAlpha = 0.9;
                    ctx.beginPath();
                    ctx.moveTo(renderStartX + (radius > 1 ? radius : 0), seg.topY);
                    ctx.lineTo(renderStartX + barWidth - (radius > 1 ? radius : 0), seg.topY);
                    ctx.stroke();
                    ctx.restore();
                } else {
                    ctx.fillRect(renderStartX, seg.topY, barWidth, seg.height);
                }

                // Subtle separator line between stacked segments (not on top)
                if (!isTopSeg && seg.height > 2) {
                    ctx.fillStyle = isDarkMode ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.2)';
                    ctx.fillRect(renderStartX, seg.bottomY - 1, barWidth, 1);
                }
            }

            //Draw the changes indicator — glowing diamond
            if (intervalData.changes && segments.length > 0) {
                const topY = segments[segments.length - 1].topY;
                const centerX = Math.round(renderStartX - 0.5 + barWidth / 2) + 0.5;
                const centerY = topY - 7;
                const halfSize = 3.5;

                ctx.save();
                ctx.shadowColor = isDarkMode ? '#c084fc' : '#7c3aed';
                ctx.shadowBlur = 8;
                ctx.fillStyle = isDarkMode ? '#e9d5ff' : '#7c3aed';
                ctx.globalAlpha = 0.95;
                ctx.beginPath();
                ctx.moveTo(centerX, centerY - halfSize);
                ctx.lineTo(centerX + halfSize, centerY);
                ctx.lineTo(centerX, centerY + halfSize);
                ctx.lineTo(centerX - halfSize, centerY);
                ctx.closePath();
                ctx.fill();
                ctx.restore();
            }
        }
        if (isFirstRender) {
            console.timeEnd('drawing canvas timeline');

        }
    }
    drawCanvasTimeline();


    /**
     * Cursor + Range selector
     */
    type RangePoint = { x: number, datum: TimelineDropsDatum };
    let rangeStartData: RangePoint | null = null;
    let rangeCrossedThreshold = false;
    let cursorRedrawTimeout: NodeJS.Timeout;
    const maskElmntId = `chartMask-${chartName}`;

    //Cursor line — glowing scan line with SVG filter
    const cursorDefs = svg.append('defs');
    const glowFilter = cursorDefs.append('filter')
        .attr('id', `glow-${chartName}`)
        .attr('x', '-50%').attr('y', '-50%')
        .attr('width', '200%').attr('height', '200%');
    glowFilter.append('feGaussianBlur')
        .attr('in', 'SourceGraphic')
        .attr('stdDeviation', '2.5')
        .attr('result', 'blur');
    const feMerge = glowFilter.append('feMerge');
    feMerge.append('feMergeNode').attr('in', 'blur');
    feMerge.append('feMergeNode').attr('in', 'SourceGraphic');

    const cursorLineVert = chartGroup.append('line')
        .attr('stroke', '#00ffd1')
        .attr('stroke-width', 1.5)
        .attr('stroke-opacity', 0.9)
        .attr('filter', `url(#glow-${chartName})`);

    //Range selector mark
    const defs = chartGroup.append('defs');
    const mask = defs.append('mask')
        .attr('id', maskElmntId);
    const maskArea = mask.append('rect')
        .attr('width', drawableAreaWidth)
        .attr('height', drawableAreaHeight)
        .attr('fill', 'white')
        .attr('class', 'transition-opacity')
        .attr('opacity', '0');
    const maskRect = mask.append('rect')
        .attr('height', drawableAreaHeight)
        .attr('fill', 'black');
    chartGroup.append('rect')
        .attr('width', drawableAreaWidth)
        .attr('height', drawableAreaHeight)
        .attr('fill', isDarkMode ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.35)')
        .attr('mask', `url(#${maskElmntId})`);

    //Helpers
    const updateRangeRect = (x1?: number, x2?: number) => {
        //Hide mask
        if (
            (typeof x1 !== 'number' || typeof x2 !== 'number')
            || (x1 < 0 && x2 < 0)
            || (x1 > drawableAreaWidth && x2 > drawableAreaWidth)
        ) {
            maskArea.attr('opacity', '0');
            return;
        }

        //Set mask
        const x1Floor = Math.floor(x1);
        const x2Floor = Math.floor(x2);
        maskRect
            .attr('x', Math.min(x1Floor, x2Floor) + 0.5 - barCenterOffset)
            .attr('width', Math.abs(x2Floor - x1Floor) + intervalWidth + 0.5);
        maskArea.attr('opacity', '1');
    }
    const setUpstreamRangeState = (range: [date1: Date, date2: Date] | null) => {
        if (!Array.isArray(range) || range.length !== 2) {
            return rangeSetter(null);
        }
        const t0 = range[0] instanceof Date ? range[0].getTime() : NaN;
        const t1 = range[1] instanceof Date ? range[1].getTime() : NaN;
        if (isNaN(t0) || isNaN(t1)) return; // guard: skip invalid dates
        if (t0 <= t1) {
            rangeSetter({
                startDate: new Date(range[0]),
                endDate: new Date(range[1]),
            });
        } else {
            rangeSetter({
                startDate: new Date(range[1]),
                endDate: new Date(range[0]),
            });
        }
    }
    const clearRangeData = (hideRect = false) => {
        rangeStartData = null;
        rangeCrossedThreshold = false;
        if (hideRect) {
            updateRangeRect();
            if (rangeSelected) {
                setUpstreamRangeState(null);
            }
        }
    }
    const hideCursor = () => {
        cursorLineVert.attr('x1', 0).attr('y1', 0).attr('x2', 0).attr('y2', 0);
        legendRef.style.opacity = '0';
        clearTimeout(cursorRedrawTimeout);
    };
    hideCursor();

    //Setting range if upstream range info available
    if (rangeSelected) {
        console.log('Upsream range selected:', rangeSelected.startDate.toISOString(), rangeSelected.endDate.toISOString());
        updateRangeRect(
            timeScale(rangeSelected.startDate),
            timeScale(rangeSelected.endDate),
        );
    }

    //Find the closest data point for a given X value
    const timeBisector = d3.bisector((interval: TimelineDropsDatum) => interval.startDate).center;
    const findClosestDatum = (pointerX: number) => {
        // const xPosDate = timeScale.invert(pointerX - intervalWidth / 2);
        const xPosDate = timeScale.invert(pointerX);
        const indexFound = timeBisector(data.log, xPosDate);
        if (indexFound === -1) return;
        const datum = data.log[indexFound];
        const datumStartTs = datum.startDate.getTime();
        return {
            datum,
            datumStartTs,
            datumStartX: timeScale(datum.startDate),
            dataumIndex: indexFound,
        };
    };

    //Detect mouse over and show timestamp + draw vertical line
    let lastDatumIndex: number | null = null;
    const handleMouseMove = (pointerX: number) => {
        // Find closest data point
        const datumFound = findClosestDatum(pointerX);
        if (!datumFound) return;
        const { datum, datumStartX, dataumIndex } = datumFound;
        if (dataumIndex === lastDatumIndex) return;
        lastDatumIndex = dataumIndex;

        //Update range selector if holding click
        if (rangeStartData) {
            rangeCrossedThreshold = true;
            return updateRangeRect(rangeStartData.x, datumStartX);
        }

        // Fire React hover datum callback
        onHoverDatum?.(datum);

        // Draw cursor — guard against NaN (datum outside scale domain)
        const cursorX = Math.round(datumStartX + intervalWidth / 2) + 0.5 - barCenterOffset;
        if (!isNaN(cursorX) && isFinite(cursorX)) {
            cursorLineVert.attr('x1', cursorX).attr('y1', 0).attr('x2', cursorX).attr('y2', drawableAreaHeight);
        }
    };

    const handleMouseDown = (pointerX: number) => {
        const datumFound = findClosestDatum(pointerX);
        if (!datumFound) return;
        hideCursor();
        rangeStartData = {
            x: datumFound.datumStartX,
            datum: datumFound.datum,
        };
        updateRangeRect(datumFound.datumStartX);
    }

    const handleMouseUp = (pointerX: number) => {
        if (!rangeStartData) return clearRangeData(true);
        const datumFound = findClosestDatum(pointerX);
        if (!datumFound) return clearRangeData(true);
        if (!rangeCrossedThreshold && rangeStartData.datum.startDate.getTime() === datumFound.datum.startDate.getTime()) {
            return clearRangeData(true);
        }

        console.log(
            'PlayerDrops range selected:',
            rangeStartData.datum.startDate,
            datumFound.datum.startDate,
        );
        clearTimeout(cursorRedrawTimeout);
        setUpstreamRangeState([rangeStartData.datum.startDate, datumFound.datum.startDate]);
        clearRangeData();
    }

    const handleMouseLeave = () => {
        setTimeout(() => {
            clearRangeData(!!rangeStartData);
            hideCursor();
            onHoverDatum?.(null);
        }, 150);
    }

    // Handle svg mouse events
    let isEventInCooldown = false;
    const cooldownTime = 20;
    chartGroup.append('rect')
        .attr('width', drawableAreaWidth)
        .attr('height', drawableAreaHeight)
        .attr('fill', 'transparent')
        .on('mousemove', function (event) {
            const [pointerX] = d3.pointer(event);
            if (!isEventInCooldown) {
                isEventInCooldown = true;
                handleMouseMove(pointerX);
                setTimeout(() => {
                    isEventInCooldown = false;
                }, cooldownTime);
            } else {
                clearTimeout(cursorRedrawTimeout);
                cursorRedrawTimeout = setTimeout(() => {
                    handleMouseMove(pointerX);
                }, cooldownTime);
            }
        })
        .on('mousedown', function (event) {
            const [pointerX] = d3.pointer(event);
            handleMouseDown(pointerX);
        })
        .on('mouseup', function (event) {
            const [pointerX] = d3.pointer(event);
            handleMouseUp(pointerX);
        });
    svg.on('mouseleave', handleMouseLeave);

    isFirstRender = false;
}
